import React from 'react';

export type BillingPeriod = 'monthly' | 'yearly';

export interface PlanFeature {
  key: string;
  included: boolean;
}

export interface Plan {
  id: string;
  icon: 'lite' | 'pro' | 'faunder';
  nameKey: string;
  descriptionKey: string;
  monthlyPrice: number | null;
  /** Фиксированная годовая цена. Месячная оплата для такого тарифа недоступна. */
  yearlyOnlyPrice?: number;
  features: PlanFeature[];
  popular?: boolean;
  exclusive?: boolean;
}

export const YEARLY_DISCOUNT = 0.2;

export const PLANS: Plan[] = [
  {
    id: 'lite',
    icon: 'lite',
    nameKey: 'subscription.lite.name',
    descriptionKey: 'subscription.lite.description',
    monthlyPrice: 490,
    features: [
      { key: 'subscription.lite.f1', included: true },
      { key: 'subscription.lite.f2', included: true },
      { key: 'subscription.lite.f3', included: true },
      { key: 'subscription.lite.f4', included: true },
    ],
  },
  {
    id: 'pro',
    icon: 'pro',
    nameKey: 'subscription.pro.name',
    descriptionKey: 'subscription.pro.description',
    monthlyPrice: 990,
    popular: true,
    features: [
      { key: 'subscription.pro.f1', included: true },
      { key: 'subscription.pro.f2', included: true },
      { key: 'subscription.pro.f3', included: true },
      { key: 'subscription.pro.f4', included: true },
    ],
  },
  {
    id: 'faunder',
    icon: 'faunder',
    nameKey: 'subscription.faunder.name',
    descriptionKey: 'subscription.faunder.description',
    monthlyPrice: null,
    yearlyOnlyPrice: 2000,
    exclusive: true,
    features: [
      { key: 'subscription.faunder.f1', included: true },
      { key: 'subscription.faunder.f2', included: true },
      { key: 'subscription.faunder.f3', included: true },
      { key: 'subscription.faunder.f4', included: true },
    ],
  },
];

export function getPlanById(planId: string): Plan | undefined {
  return PLANS.find((p) => p.id === planId);
}

export function formatPrice(value: number): string {
  return value.toLocaleString('ru-RU');
}

export function getDisplayPrice(monthlyPrice: number, billing: BillingPeriod): number {
  if (billing === 'monthly') return monthlyPrice;
  return Math.round(monthlyPrice * (1 - YEARLY_DISCOUNT));
}

export function getTotalPrice(monthlyPrice: number, billing: BillingPeriod): number {
  const perMonth = getDisplayPrice(monthlyPrice, billing);
  return billing === 'yearly' ? perMonth * 12 : perMonth;
}

export function isYearlyOnlyPlan(plan: Plan): boolean {
  return typeof plan.yearlyOnlyPrice === 'number';
}

/** Сумма к оплате за выбранный период. Годовой тариф не зависит от переключателя месяц/год. */
export function getChargeAmount(plan: Plan, billing: BillingPeriod): number {
  if (plan.yearlyOnlyPrice != null) return plan.yearlyOnlyPrice;
  if (plan.monthlyPrice == null) return 0;
  return getTotalPrice(plan.monthlyPrice, billing);
}

export function PlanIcon({ type, className = 'sub-plan-icon' }: { type: Plan['icon']; className?: string }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 };

  if (type === 'lite') {
    return (
      <svg className={className} viewBox="0 0 48 48" aria-hidden>
        <path {...common} d="M24 8 L38 16 V32 L24 40 L10 32 V16 Z" />
        <path {...common} d="M24 8 V40 M10 16 L38 32 M38 16 L10 32" opacity="0.5" />
      </svg>
    );
  }

  if (type === 'pro') {
    return (
      <svg className={className} viewBox="0 0 48 48" aria-hidden>
        <path {...common} d="M24 6 L40 14 V34 L24 42 L8 34 V14 Z" />
        <circle {...common} cx="24" cy="24" r="8" />
        <path {...common} d="M24 16 V32 M16 24 H32" opacity="0.45" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden>
      <path {...common} d="M24 6 L28.5 18.2 L41.5 18.2 L31 25.8 L35 38 L24 30.6 L13 38 L17 25.8 L6.5 18.2 L19.5 18.2 Z" />
      <circle {...common} cx="24" cy="23" r="4" />
    </svg>
  );
}
